import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import DateTimePicker from '@react-native-community/datetimepicker';
import theme from '../../theme';
import { AssignmentHistory, Driver } from '../../types';
import { updateAssignmentHistory } from '../../api/supabase';
import { useAssignmentStore } from '../../stores/assignmentStore';
import { useDriverStore } from '../../stores/driverStore';

const EditAssignmentScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const assignment = route.params?.assignment;
  const { fetchAssignments } = useAssignmentStore();
  const { drivers } = useDriverStore();

  const [loading, setLoading] = useState(false);
  const [assignmentDate, setAssignmentDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [monthlyRate, setMonthlyRate] = useState('');
  const [dailyRate, setDailyRate] = useState('');
  const [status, setStatus] = useState<'active' | 'completed' | 'cancelled'>('active');
  const [secondaryDriverId, setSecondaryDriverId] = useState<string | null>(null);
  const [showAssignmentDatePicker, setShowAssignmentDatePicker] = useState(false);
  const [showReturnDatePicker, setShowReturnDatePicker] = useState(false);

  const availableDrivers = drivers.filter(d => d.status === 'active');

  useEffect(() => {
    if (assignment) {
      setAssignmentDate(assignment.assignment_date?.split('T')[0] || '');
      setReturnDate(assignment.return_date?.split('T')[0] || '');
      setMonthlyRate(assignment.monthly_rent_rate?.toString() || '0');
      setDailyRate(assignment.daily_rate?.toString() || '0');
      setStatus(assignment.status || 'active');
      setSecondaryDriverId(assignment.secondary_driver_id || null);
    }
  }, [assignment]);

  if (!assignment) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>No assignment data available</Text>
      </View>
    );
  }

  const handleMonthlyRateChange = (value: string) => {
    setMonthlyRate(value);
    const monthly = parseFloat(value) || 0;
    if (monthly > 0) {
      const daily = Math.round((monthly / 30) * 100) / 100;
      setDailyRate(daily.toString());
    }
  };

  const handleSave = async () => {
    if (!assignmentDate) {
      Alert.alert('خطأ / Error', 'Assignment date is required / تاريخ التعيين مطلوب');
      return;
    }

    const updatedData: Partial<AssignmentHistory> = {};
    
    if (assignmentDate !== assignment.assignment_date?.split('T')[0]) {
      updatedData.assignment_date = assignmentDate;
    }
    if (returnDate !== (assignment.return_date?.split('T')[0] || '')) {
      updatedData.return_date = returnDate || undefined;
    }
    if (parseFloat(monthlyRate) !== (assignment.monthly_rent_rate || 0)) {
      updatedData.monthly_rent_rate = parseFloat(monthlyRate) || 0;
    }
    if (parseFloat(dailyRate) !== (assignment.daily_rate || 0)) {
      updatedData.daily_rate = parseFloat(dailyRate) || 0;
    }
    if (status !== assignment.status) {
      updatedData.status = status;
    }
    if (secondaryDriverId !== assignment.secondary_driver_id) {
      updatedData.secondary_driver_id = secondaryDriverId || undefined;
      updatedData.is_shared = !!secondaryDriverId;
    }

    if (Object.keys(updatedData).length === 0) {
      Alert.alert('معلومات / Info', 'No changes were made / لم يتم إجراء أي تغييرات');
      navigation.goBack();
      return;
    }

    try {
      setLoading(true);
      console.log('💾 Saving assignment changes:', updatedData);
      
      await updateAssignmentHistory(assignment.id, updatedData);
      
      console.log('✅ Assignment updated successfully');
      
      // Refresh assignments first
      await fetchAssignments();
      
      // Navigate back immediately (don't wait for Alert on web)
      navigation.goBack(); // Go back to ManageAssignment
      navigation.goBack(); // Go back to AssignmentHistory
      
      // Show success message after navigation (setTimeout ensures navigation completes first)
      setTimeout(() => {
        Alert.alert(
          'نجح / Success',
          'تم تحديث التعيين بنجاح\nAssignment updated successfully'
        );
      }, 300);
    } catch (error) {
      console.error('❌ Error updating assignment:', error);
      Alert.alert(
        'خطأ / Error',
        'فشل تحديث التعيين\nFailed to update assignment'
      );
    } finally {
      setLoading(false);
    }
  };

  const onAssignmentDateChange = (event: any, selectedDate?: Date) => {
    setShowAssignmentDatePicker(false);
    if (selectedDate) {
      setAssignmentDate(selectedDate.toISOString().split('T')[0]);
    }
  };

  const onReturnDateChange = (event: any, selectedDate?: Date) => {
    setShowReturnDatePicker(false);
    if (selectedDate) {
      setReturnDate(selectedDate.toISOString().split('T')[0]);
    }
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>تعديل التعيين / Edit Assignment</Text>
          <Text style={styles.headerSubtitle}>
            {assignment.vehicle?.license_plate} - {assignment.primaryDriver?.name}
          </Text>
        </View>
      </View>

      <ScrollView style={styles.content}>
        {/* Read-only Info */}
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>معلومات ثابتة / Fixed Information</Text>
          
          <View style={styles.readOnlyField}>
            <Text style={styles.label}>المركبة / Vehicle</Text>
            <Text style={styles.readOnlyValue}>
              {assignment.vehicle?.license_plate} - {assignment.vehicle?.make_model}
            </Text>
          </View>

          <View style={styles.readOnlyField}>
            <Text style={styles.label}>السائق الأساسي / Primary Driver</Text>
            <Text style={styles.readOnlyValue}>{assignment.primaryDriver?.name}</Text>
          </View>
        </View>

        {/* Editable Fields */}
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>التفاصيل القابلة للتعديل / Editable Details</Text>

          {/* Assignment Date */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>تاريخ التعيين / Assignment Date *</Text>
            <TouchableOpacity 
              style={styles.dateInput}
              onPress={() => setShowAssignmentDatePicker(true)}
            >
              <Ionicons name="calendar-outline" size={20} color={theme.colors.primary} />
              <Text style={styles.dateText}>
                {assignmentDate || 'Select date'}
              </Text>
            </TouchableOpacity>
            {showAssignmentDatePicker && (
              <DateTimePicker
                value={assignmentDate ? new Date(assignmentDate) : new Date()}
                mode="date"
                display="default"
                onChange={onAssignmentDateChange}
              />
            )}
          </View>

          {/* Return Date */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>تاريخ الإرجاع / Return Date (Optional)</Text>
            <TouchableOpacity 
              style={styles.dateInput}
              onPress={() => setShowReturnDatePicker(true)}
            >
              <Ionicons name="calendar-outline" size={20} color={theme.colors.primary} />
              <Text style={styles.dateText}>
                {returnDate || 'Not set'}
              </Text>
            </TouchableOpacity>
            {showReturnDatePicker && (
              <DateTimePicker
                value={returnDate ? new Date(returnDate) : new Date()}
                mode="date"
                display="default"
                onChange={onReturnDateChange}
              />
            )}
          </View>

          {/* Monthly Rate */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>الحصة الشهرية / Monthly Company Share</Text>
            <TextInput
              style={styles.input}
              value={monthlyRate}
              onChangeText={handleMonthlyRateChange}
              keyboardType="numeric"
              placeholder="0"
            />
          </View>

          {/* Daily Rate */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>الأجرة اليومية / Daily Rate</Text>
            <TextInput
              style={styles.input}
              value={dailyRate}
              onChangeText={setDailyRate}
              keyboardType="numeric"
              placeholder="0"
            />
            <Text style={styles.helperText}>
              Auto-calculated from monthly rate (Monthly ÷ 30)
            </Text>
          </View>

          {/* Status */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>الحالة / Status</Text>
            <View style={styles.statusOptions}>
              <TouchableOpacity
                style={[styles.statusOption, status === 'active' && styles.statusOptionActive]}
                onPress={() => setStatus('active')}
              >
                <Text style={[styles.statusOptionText, status === 'active' && styles.statusOptionTextActive]}>
                  نشط / Active
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.statusOption, status === 'completed' && styles.statusOptionActive]}
                onPress={() => setStatus('completed')}
              >
                <Text style={[styles.statusOptionText, status === 'completed' && styles.statusOptionTextActive]}>
                  مكتمل / Completed
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.statusOption, status === 'cancelled' && styles.statusOptionActive]}
                onPress={() => setStatus('cancelled')}
              >
                <Text style={[styles.statusOptionText, status === 'cancelled' && styles.statusOptionTextActive]}>
                  ملغى / Cancelled
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Secondary Driver (optional) */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>السائق الثانوي / Secondary Driver (Optional)</Text>
            <View style={styles.driverOptions}>
              <TouchableOpacity
                style={[styles.driverOption, !secondaryDriverId && styles.driverOptionActive]}
                onPress={() => setSecondaryDriverId(null)}
              >
                <Text style={[styles.driverOptionText, !secondaryDriverId && styles.driverOptionTextActive]}>
                  None
                </Text>
              </TouchableOpacity>
              {availableDrivers
                .filter(d => d.id !== assignment.primary_driver_id)
                .slice(0, 3)
                .map(driver => (
                  <TouchableOpacity
                    key={driver.id}
                    style={[styles.driverOption, secondaryDriverId === driver.id && styles.driverOptionActive]}
                    onPress={() => setSecondaryDriverId(driver.id)}
                  >
                    <Text style={[styles.driverOptionText, secondaryDriverId === driver.id && styles.driverOptionTextActive]}>
                      {driver.name}
                    </Text>
                  </TouchableOpacity>
                ))}
            </View>
          </View>
        </View>

        {/* Action Buttons at Bottom */}
        <View style={styles.actionButtons}>
          <TouchableOpacity 
            style={styles.cancelButton}
            onPress={() => navigation.goBack()}
            disabled={loading}
          >
            <Text style={styles.cancelButtonText}>إلغاء / Cancel</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={[styles.saveButton, loading && styles.saveButtonDisabled]}
            onPress={handleSave}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator size="small" color={theme.colors.white} />
            ) : (
              <Text style={styles.saveButtonText}>حفظ / Save</Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    paddingTop: 50,
    paddingHorizontal: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  backButton: {
    padding: theme.spacing.sm,
    marginRight: theme.spacing.sm,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.gray600,
  },
  content: {
    flex: 1,
    padding: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  readOnlyField: {
    marginBottom: theme.spacing.md,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.gray700,
    marginBottom: theme.spacing.xs,
  },
  readOnlyValue: {
    fontSize: 15,
    color: theme.colors.gray600,
    backgroundColor: theme.colors.gray50,
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
  },
  inputGroup: {
    marginBottom: theme.spacing.md,
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.sm,
    fontSize: 15,
    color: theme.colors.textPrimary,
    backgroundColor: theme.colors.white,
  },
  dateInput: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.white,
  },
  dateText: {
    fontSize: 15,
    color: theme.colors.textPrimary,
    marginLeft: theme.spacing.sm,
  },
  helperText: {
    fontSize: 12,
    color: theme.colors.gray500,
    marginTop: 4,
    fontStyle: 'italic',
  },
  statusOptions: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  statusOption: {
    flex: 1,
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
    alignItems: 'center',
  },
  statusOptionActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  statusOptionText: {
    fontSize: 14,
    color: theme.colors.gray600,
  },
  statusOptionTextActive: {
    color: theme.colors.white,
    fontWeight: '600',
  },
  driverOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
  },
  driverOption: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  driverOptionActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  driverOptionText: {
    fontSize: 14,
    color: theme.colors.gray600,
  },
  driverOptionTextActive: {
    color: theme.colors.white,
    fontWeight: '600',
  },
  errorText: {
    fontSize: 16,
    color: theme.colors.error,
    textAlign: 'center',
    marginTop: 50,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    padding: theme.spacing.md,
    paddingTop: theme.spacing.lg,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.gray700,
  },
  saveButton: {
    flex: 1,
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: theme.colors.gray400,
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.white,
  },
});

export default EditAssignmentScreen;
